import { getUnixTime } from 'date-fns';
import { debounceTime, map, switchMap, of } from 'rxjs';
import { ADMIN_API } from '../AdminAPI';
import { convertUnixToDate, createBranch, Branch, getBranchConfig, WithdrawWayType } from '@golden/gdk-base';
export function createFinanceService(api, trunk) {
    return {
        supportBanks: createBranch({
            trunk,
            branch: Branch.SUPPORT_BANK,
            value: [],
            operator: () => api.fetch(ADMIN_API.GET_SUPPORT_BANK)
        }),
        forwarders: createBranch({
            trunk,
            branch: Branch.FORWARDERS,
            value: {},
            operator: () => api.fetch(ADMIN_API.GET_FINANCE_ACTIVE_FORWARDERS)
        }),
        alert: createBranch({
            trunk,
            branch: getBranchConfig[Branch.ADMIN_FINANCE_ALERT](),
            value: { withdraw_ids: [], rc_withdraw_ids: [] },
            operator: (obs) => obs.pipe(debounceTime(100), switchMap((param) => {
                if (param.openedAt) {
                    return api.fetch(ADMIN_API.GET_FINANCE_ALERT, { data: { opened_at: getUnixTime(param.openedAt) } });
                }
                return of({ withdraw_ids: [], rc_withdraw_ids: [] });
            }))
        }),
        getFinanceAlert(time) {
            return api.fetch(ADMIN_API.GET_FINANCE_ALERT, { data: { opened_at: getUnixTime(time) } });
        },
        getBankList(data) {
            return api.fetch(ADMIN_API.GET_BANK_LIST, { data });
        },
        getBank(id) {
            return api.fetch(ADMIN_API.GET_BANK, { config: { urlParams: { id } } });
        },
        updateBank(id, data) {
            return api.fetch(ADMIN_API.PUT_BANK, { data, config: { urlParams: { id } } });
        },
        maintainBank(id, maintained) {
            return api.fetch(ADMIN_API.PUT_BANK_MAINTAIN, { data: { maintained }, config: { urlParams: { id } } });
        },
        getDebitCardBlacklist(payload) {
            const data = { ...payload };
            if (data.start_at)
                data.start_at = getUnixTime(data.start_at);
            if (data.end_at)
                data.end_at = getUnixTime(data.end_at);
            return api.fetch(ADMIN_API.GET_FINANCE_DEBIT_CARD_BLACKLIST, { data }).pipe(map((res) => ({
                ...res,
                data: res.data.map((item) => convertUnixToDate(item, 'created_at'))
            })));
        },
        validateBlackListDebitCard(data) {
            return api.fetch(ADMIN_API.POST_VALIDATE_BLACKLIST_DEBIT_CARD, { data });
        },
        getDebitCardInfo(data) {
            return api.fetch(ADMIN_API.GET_ACCOUNT_NUMBER_INFO, { data });
        },
        createBlacklist(data) {
            return api.fetch(ADMIN_API.POST_FINANCE_BLACKLIST, { data });
        },
        deleteBlacklist(id) {
            return api.fetch(ADMIN_API.DELETE_FINANCE_BLACKLIST, { config: { urlParams: { id } } });
        },
        getWithdrawalAccountBlacklist(payload) {
            const data = { ...payload };
            if (data.start_at)
                data.start_at = getUnixTime(data.start_at);
            if (data.end_at)
                data.end_at = getUnixTime(data.end_at);
            return api.fetch(ADMIN_API.GET_FINANCE_WITHDRAWAL_ACCOUNT_BLACKLIST, { data }).pipe(map((res) => ({
                ...res,
                data: res.data.map((item) => convertUnixToDate(item, 'created_at'))
            })));
        },
        createWithdrawalAccountBlacklist(data) {
            const { id, memo } = data;
            return api.fetch(ADMIN_API.POST_FINANCE_WITHDRAWAL_ACCOUNT_BLACKLIST, { data: { memo }, config: { urlParams: { id } } });
        },
        deleteWithdrawalAccountBlacklist(id) {
            return api.fetch(ADMIN_API.DELETE_FINANCE_WITHDRAWAL_ACCOUNT_BLACKLIST, { config: { urlParams: { id } } });
        },
        validateWithdrawalAccount(account) {
            return api.fetch(ADMIN_API.VALIDATE_PLAYER_WITHDRAWAL_ACCOUNT, { data: { protocol: WithdrawWayType.CURRENCY_ALIPAY, account } });
        },
        getCourierList(data) {
            return api.fetch(ADMIN_API.GET_FINANCE_COURIER_LIST, { data }).pipe(map((res) => ({
                ...res,
                data: res.data.map((item) => convertUnixToDate(item, 'updated_at'))
            })));
        },
        getCourier(id) {
            return api.fetch(ADMIN_API.GET_FINANCE_COURIER, { config: { urlParams: { id } } }).pipe(map(res => convertUnixToDate(res, 'updated_at')));
        },
        createCourier(data) {
            return api.fetch(ADMIN_API.POST_FINANCE_COURIER, { data });
        },
        updateCourier(id, data) {
            return api.fetch(ADMIN_API.PUT_FINANCE_COURIER, { data, config: { urlParams: { id } } });
        },
        getForwarderConfig() {
            return api.fetch(ADMIN_API.GET_FINANCE_FORWARDER_CONFIG);
        },
        getSupportMethods() {
            return api.fetch(ADMIN_API.GET_FINANCE_COURIERS_METHODS);
        },
        getDepositWayList(data) {
            return api.fetch(ADMIN_API.GET_DEPOSIT_WAY_LIST, { data });
        },
        deleteDepositWay(id) {
            return api.fetch(ADMIN_API.DELETE_DEPOSIT_WAY, { config: { urlParams: { id } } });
        },
        getWithdrawalWayList(data) {
            return api.fetch(ADMIN_API.GET_WITHDRAWAL_WAY_LIST, { data });
        },
        getDepositWay(id) {
            return api.fetch(ADMIN_API.GET_DEPOSIT_WAY, { config: { urlParams: { id } } });
        },
        getWithdrawalWay(id) {
            return api.fetch(ADMIN_API.GET_WITHDRAWAL_WAY, { config: { urlParams: { id } } });
        },
        createDepositWay(data) {
            return api.fetch(ADMIN_API.POST_DEPOSIT_WAY, { data });
        },
        createWithdrawalWay(data) {
            return api.fetch(ADMIN_API.POST_WITHDRAWAL_WAY, { data });
        },
        updateDepositWay(id, data) {
            return api.fetch(ADMIN_API.PUT_DEPOSIT_WAY, { data, config: { urlParams: { id } } });
        },
        updateWithdrawalWay(id, data) {
            return api.fetch(ADMIN_API.PUT_WITHDRAWAL_WAY, { data, config: { urlParams: { id } } });
        },
        deleteWithdrawWay(id) {
            return api.fetch(ADMIN_API.DELETE_WITHDRAWAL_WAY, { config: { urlParams: { id } } });
        },
        getFinanceFeeList() {
            return api.fetch(ADMIN_API.GET_FINANCE_FEE_LIST).pipe(map((res) => res.map((item) => convertUnixToDate(item, 'updated_at'))));
        },
        getFinanceFee(id) {
            return api.fetch(ADMIN_API.GET_FINANCE_FEE, { config: { urlParams: { id } } }).pipe(map((res) => convertUnixToDate(res, 'updated_at')));
        },
        updateFinanceFee(id, data) {
            return api.fetch(ADMIN_API.PUT_FINANCE_FEE, { data, config: { urlParams: { id } } });
        },
        getPiggyBankList(data) {
            return api.fetch(ADMIN_API.GET_PIGGY_BANK_LIST, { data }).pipe(map((res) => ({
                ...res,
                data: res.data.map((item) => convertUnixToDate(item, 'updated_at'))
            })));
        },
        createPiggyBank(data) {
            return api.fetch(ADMIN_API.POST_PIGGY_BANK, { data });
        },
        getPiggyBankOptions() {
            return api.fetch(ADMIN_API.GET_PIGGY_BANK_BANK_OPTIONS);
        },
        getPiggyBank(id) {
            return api.fetch(ADMIN_API.GET_PIGGY_BANK, { config: { urlParams: { id } } });
        },
        updatePiggyBank(id, data) {
            return api.fetch(ADMIN_API.PUT_PIGGY_BANK, { data, config: { urlParams: { id } } });
        },
        togglePiggyBank(id, data) {
            return api.fetch(ADMIN_API.PUT_PIGGY_BANK_ACTIVE, { data, config: { urlParams: { id } } });
        },
        deletePiggyBank(id) {
            return api.fetch(ADMIN_API.DELETE_PIGGY_BANK, { config: { urlParams: { id } } });
        },
        getPiggyWechatList(data) {
            return api.fetch(ADMIN_API.GET_PIGGY_WECHAT_LIST, { data }).pipe(map((res) => ({
                ...res,
                data: res.data.map((item) => convertUnixToDate(item, 'updated_at'))
            })));
        },
        createPiggyWechat(data) {
            return api.fetch(ADMIN_API.POST_PIGGY_WECHAT, { data });
        },
        getPiggyWechat(id) {
            return api.fetch(ADMIN_API.GET_PIGGY_WECHAT, { config: { urlParams: { id } } });
        },
        updatePiggyWechat(id, data) {
            return api.fetch(ADMIN_API.PUT_PIGGY_WECHAT, { data, config: { urlParams: { id } } });
        },
        togglePiggyWechat(id, data) {
            return api.fetch(ADMIN_API.PUT_PIGGY_WECHAT_ACTIVE, { data, config: { urlParams: { id } } });
        },
        deletePiggyWechat(id) {
            return api.fetch(ADMIN_API.DELETE_PIGGY_WECHAT, { config: { urlParams: { id } } });
        },
        getPiggyAlipayList(data) {
            return api.fetch(ADMIN_API.GET_PIGGY_ALIPAY_LIST, { data }).pipe(map((res) => ({
                ...res,
                data: res.data.map((item) => convertUnixToDate(item, 'updated_at'))
            })));
        },
        createPiggyAlipay(data) {
            return api.fetch(ADMIN_API.POST_PIGGY_ALIPAY, { data });
        },
        getPiggyAlipay(id) {
            return api.fetch(ADMIN_API.GET_PIGGY_ALIPAY, { config: { urlParams: { id } } });
        },
        updatePiggyAlipay(id, data) {
            return api.fetch(ADMIN_API.PUT_PIGGY_ALIPAY, { data, config: { urlParams: { id } } });
        },
        togglePiggyAlipay(id, data) {
            return api.fetch(ADMIN_API.PUT_PIGGY_ALIPAY_ACTIVE, { config: { urlParams: { id } }, data });
        },
        deletePiggyAlipay(id) {
            return api.fetch(ADMIN_API.DELETE_PIGGY_ALIPAY, { config: { urlParams: { id } } });
        },
        getExchaneRateDefault(data) {
            return api.fetch(ADMIN_API.GET_EXCHANGE_RATE_DEFAULT, { data });
        },
        updateExchaneRateDefault(data) {
            return api.fetch(ADMIN_API.POST_EXCHANGE_RATE_DEFAULT, { data });
        },
        getExchaneRateDiff() {
            return api.fetch(ADMIN_API.GET_EXCHANGE_RATE_DIFF);
        },
        updateExchaneRateDiff(data) {
            return api.fetch(ADMIN_API.POST_EXCHANGE_RATE_DIFF, { data });
        },
        getExchaneRateProviders(data) {
            return api.fetch(ADMIN_API.GET_EXCHANGE_RATE_PROVIDER, { data }).pipe(map((res) => res.map((item) => convertUnixToDate(item, 'last_fetched_at'))));
        },
        updateExchaneRateProviders(data) {
            return api.fetch(ADMIN_API.PUT_EXCHANGE_RATE_PROVIDER, { data });
        },
        getBanksMappingForwarderList() {
            return api.fetch(ADMIN_API.GET_BANKS_MAPPING_FORWARDER_LIST);
        },
        getBanksMappingForwarder(slug) {
            return api.fetch(ADMIN_API.GET_BANKS_MAPPING_FORWARDER, { config: { urlParams: { slug } } });
        },
        updateBanksMappingForwarder(slug, data) {
            return api.fetch(ADMIN_API.PUT_BANKS_MAPPING_FORWARDER, { data, config: { urlParams: { slug } } });
        },
        getDepositFeeMethod() {
            return api.fetch(ADMIN_API.GET_DEPOSIT_FEE_METHOD);
        },
        getDepositQueryList(data) {
            return api.fetch(ADMIN_API.GET_DEPOSIT_QUERY_LIST, { data });
        },
        getWithdrawFeeMethod() {
            return api.fetch(ADMIN_API.GET_WITHDRAW_FEE_METHOD);
        },
        getWithdrawQueryList(data) {
            return api.fetch(ADMIN_API.GET_WITHDRAW_QUERY_LIST, { data });
        },
        getDepositMethodPriorities() {
            return api.fetch(ADMIN_API.GET_DEPOSIT_METHOD_PRIORITIES);
        },
        updateDepositMethodPriorities(data) {
            return api.fetch(ADMIN_API.PUT_DEPOSIT_METHOD_PRIORITIES, { data });
        },
        getWithdrawalMethodPriorities() {
            return api.fetch(ADMIN_API.GET_WITHDRAWAL_METHOD_PRIORITIES);
        },
        updateWithdrawalMethodPriorities(data) {
            return api.fetch(ADMIN_API.PUT_WITHDRAWAL_METHOD_PRIORITIES, { data });
        },
        getDepositWayGroupList(data) {
            return api.fetch(ADMIN_API.GET_FINANCE_DEPOSIT_WAY_GROUPS_LIST, { data }).pipe(map((res) => ({
                ...res,
                data: res.data.map((item) => convertUnixToDate(item, 'operated_at'))
            })));
        },
        getDepositWayGroup(id) {
            return api.fetch(ADMIN_API.GET_FINANCE_DEPOSIT_WAY_GROUP, { config: { urlParams: { id } } }).pipe(map((res) => convertUnixToDate(res, 'operated_at')));
        },
        createDepositWayGroup(data) {
            return api.fetch(ADMIN_API.POST_FINANCE_DEPOSIT_WAY_GROUP, { data });
        },
        updateDepositWayGroup(id, data) {
            return api.fetch(ADMIN_API.PUT_FINANCE_DEPOSIT_WAY_GROUP, { data, config: { urlParams: { id } } });
        },
        deleteDepositWayGroup(id) {
            return api.fetch(ADMIN_API.DELETE_FINANCE_DEPOSIT_WAY_GROUP, { config: { urlParams: { id } } });
        },
        getDepositWayGroupSearch(data) {
            return api.fetch(ADMIN_API.GET_FINANCE_DEPOSIT_WAY_GROUPS_SEARCH, { data });
        }
    };
}
//# sourceMappingURL=FinanceService.js.map